/**
 * Page Comment Form Component
 * Form for detailed page-specific feedback
 */

import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Feedback, PageCommentFeedback } from '../../../types/feedback';
import ScreenshotButton from '../ScreenshotButton';

interface PageCommentFormProps {
  data: Partial<Feedback>;
  onChange: (data: Partial<PageCommentFeedback>) => void;
  validation: { isValid: boolean; errors: Record<string, string> };
  currentStep: number;
  totalSteps: number;
  enableScreenshot?: boolean;
}

const PageCommentForm: React.FC<PageCommentFormProps> = ({
  data,
  onChange,
  validation,
  currentStep,
  enableScreenshot = true
}) => {
  const commentData = data as Partial<PageCommentFeedback>;
  const [charCount, setCharCount] = useState(commentData.comment?.length || 0);
  const [screenshot, setScreenshot] = useState<any>(null);

  const categories = [
    { 
      key: 'data-accuracy', 
      label: 'Data Accuracy', 
      description: 'Information quality, completeness, or correctness',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/>
        </svg>
      )
    },
    { 
      key: 'visualization', 
      label: 'Visualization', 
      description: 'Charts, maps, graphs, and visual representations',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
          <path d="M9 9h.01"/>
          <path d="M15 9h.01"/>
          <path d="M9 15h.01"/>
          <path d="M15 15h.01"/>
        </svg>
      )
    },
    { 
      key: 'navigation', 
      label: 'Navigation', 
      description: 'Menu, links, page flow, and user interface',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <polyline points="9,22 9,12 15,12 15,22"/>
        </svg>
      )
    },
    { 
      key: 'performance', 
      label: 'Performance', 
      description: 'Loading speed, responsiveness, and technical issues',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r={10}/>
          <polyline points="12,6 12,12 16,14"/>
        </svg>
      )
    },
    { 
      key: 'other', 
      label: 'Other', 
      description: 'General feedback or suggestions',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 12h.01"/>
          <path d="M12 12h.01"/>
          <path d="M16 12h.01"/>
        </svg>
      )
    }
  ];

  const priorities = [
    { key: 'low', label: 'Low', description: 'Nice to have improvement', color: 'text-sage-600' },
    { key: 'medium', label: 'Medium', description: 'Would improve experience', color: 'text-amber-600' },
    { key: 'high', label: 'High', description: 'Important issue to address', color: 'text-coral-600' }
  ];

  const handleCommentChange = (comment: string) => {
    setCharCount(comment.length);
    onChange({
      ...commentData,
      comment,
      type: 'page-comment'
    });
  };

  const handleCategoryChange = (category: PageCommentFeedback['category']) => {
    onChange({
      ...commentData,
      category
    });
  };

  const handlePriorityChange = (priority: PageCommentFeedback['priority']) => {
    onChange({
      ...commentData,
      priority
    });
  };

  const handleScreenshotCapture = (screenshotData: any) => {
    setScreenshot(screenshotData);
    onChange({
      ...commentData,
      attachments: screenshotData.thumbnail ? [{
        type: 'screenshot',
        url: screenshotData.thumbnail,
        metadata: screenshotData.metadata
      }] : []
    });
  };

  if (currentStep === 1) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-serif text-slate-800 mb-2">
            Share your thoughts about this page
          </h3>
          <p className="text-sm text-slate-600">
            Your detailed feedback helps us improve the humanitarian data platform
          </p>
        </div>

        {/* Comment Text Area */}
        <div className="space-y-3">
          <label htmlFor="page-comment" className="block text-sm font-medium text-slate-700">
            Your feedback
          </label>
          <div className="relative">
            <textarea
              id="page-comment"
              rows={6}
              value={commentData.comment || ''}
              onChange={(e) => handleCommentChange(e.target.value)}
              placeholder="Tell us what you think about this page. Be specific about what works well, what doesn't, or what could be improved..."
              className={cn(
                'input-minimal resize-none pr-16',
                validation.errors.comment && 'border-coral-300 focus:border-coral-400'
              )}
              maxLength={2000}
            />
            <div className="absolute bottom-3 right-3 text-xs text-slate-400">
              {charCount}/2000
            </div>
          </div>
          {validation.errors.comment && (
            <p className="text-sm text-coral-600">{validation.errors.comment}</p>
          )}
        </div>

        {/* Category Selection */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-slate-700">
            What category best describes your feedback?
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {categories.map((category) => (
              <button
                key={category.key}
                onClick={() => handleCategoryChange(category.key as PageCommentFeedback['category'])}
                className={cn(
                  'flex items-start gap-3 p-4 text-left border-2 rounded-lg transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2',
                  commentData.category === category.key
                    ? 'border-terracotta bg-terracotta-50'
                    : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                )}
              >
                <div className="text-slate-600 mt-1">
                  {category.icon}
                </div>
                <div className="min-w-0">
                  <h4 className="font-medium text-slate-800 mb-1">
                    {category.label}
                  </h4>
                  <p className="text-sm text-slate-600 leading-snug">
                    {category.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Screenshot Button */}
        {enableScreenshot && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">
              Add a screenshot (optional)
            </label>
            <ScreenshotButton
              onScreenshotCapture={handleScreenshotCapture}
              existingScreenshot={screenshot?.thumbnail}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-serif text-slate-800 mb-2">
          Additional details
        </h3>
        <p className="text-sm text-slate-600">
          Help us prioritize and categorize your feedback
        </p>
      </div>

      {/* Priority Selection */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-slate-700">
          How important is this feedback?
        </label>
        <div className="space-y-2">
          {priorities.map((priority) => (
            <button
              key={priority.key}
              onClick={() => handlePriorityChange(priority.key as PageCommentFeedback['priority'])}
              className={cn(
                'w-full flex items-center gap-3 p-3 text-left border-2 rounded-lg transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2',
                commentData.priority === priority.key
                  ? 'border-terracotta bg-terracotta-50'
                  : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
              )}
            >
              <div className={cn('w-3 h-3 rounded-full', 
                priority.key === 'low' && 'bg-sage-400',
                priority.key === 'medium' && 'bg-amber-400',
                priority.key === 'high' && 'bg-coral-400'
              )} />
              <div>
                <span className={cn('font-medium', priority.color)}>
                  {priority.label}
                </span>
                <p className="text-sm text-slate-600 mt-0.5">
                  {priority.description}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Review */}
      <div className="bg-stone-50 p-4 rounded-lg">
        <h4 className="text-sm font-medium text-slate-800 mb-2">Review your feedback</h4>
        <div className="space-y-2 text-sm">
          <p><span className="font-medium">Category:</span> {categories.find(c => c.key === commentData.category)?.label}</p>
          <p><span className="font-medium">Priority:</span> {priorities.find(p => p.key === commentData.priority)?.label}</p>
          <div>
            <span className="font-medium">Comment:</span>
            <p className="mt-1 text-slate-600 italic line-clamp-3">
              "{commentData.comment}"
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageCommentForm;